import {
  DeviceLabels,
  useMeetingManager,
} from 'amazon-chime-sdk-component-library-react';
import { MeetingSessionConfiguration } from 'amazon-chime-sdk-js';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

export const VOICE_ONLY_MEETING_TYPE = 1;

export enum MeetingSidebar {
  INFO = 'info',
  ATTENDEES = 'attendees',
  NOTES = 'notes',
}

export enum MeetingPage {
  PRE_VIDEO_CALL = 'PRE_VIDEO_CALL',
  VIDEO_CALL = 'VIDEO_CALL',
  VIDEO_CALL_END = 'VIDEO_CALL_END',
}

export type MeetingSidebarType = MeetingSidebar | null;
export type MeetingPageType = MeetingPage | null;

export interface IMeeting {
  MeetingId: string;
  ExternalMeetingId: string;
  MediaRegion: string;
  MediaPlacement: {
    AudioHostUrl: string;
    AudioFallbackUrl: string;
    SignalingUrl: string;
    TurnControlUrl: string;
    ScreenDataUrl: string;
    ScreenViewingUrl: string;
    ScreenSharingUrl: string;
    EventIngestionUrl: string;
  };
  TenantIds: unknown[];
  MeetingArn: string;
}

export interface IAttendee {
  ExternalUserId: string;
  AttendeeId: string;
  JoinToken: string;
  Capabilities: {
    Audio: string;
    Video: string;
    Content: string;
  };
}

interface IMeetingDetails {
  meeting_id: number;
  meeting_type: number;
  report_id: string;
  Meeting: IMeeting;
  Attendee: IAttendee;
  report_reference_id: string;
}

interface IMeetingContext {
  attendeeID: string;
  openSidebar: MeetingSidebarType;
  meetingPage: MeetingPageType;
  isMeetingEnded: boolean;
  endMeetingCustomMessage: string;
  isVoiceOnlyCall: boolean;
  meetingDetails: React.MutableRefObject<IMeetingDetails | null>;
  mediaPipelineID: React.MutableRefObject<string>;
  setAttendeeID: React.Dispatch<React.SetStateAction<string>>;
  setOpenSidebar: React.Dispatch<React.SetStateAction<MeetingSidebarType>>;
  setMeetingPage: React.Dispatch<React.SetStateAction<MeetingPageType>>;
  setIsMeetingEnded: React.Dispatch<React.SetStateAction<boolean>>;
  setEndMeetingCustomMessage: React.Dispatch<React.SetStateAction<string>>;
  setAttendees: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setIsVoiceOnlyCall: React.Dispatch<React.SetStateAction<boolean>>;
  joinMeeting: () => Promise<void>;
  startMeeting: () => Promise<void>;
}

const MeetingContext = createContext<IMeetingContext | undefined>(undefined);

export function MeetingContextProvider({ children }: { children: ReactNode }) {
  const meetingManager = useMeetingManager();
  const [attendeeID, setAttendeeID] = useState('');
  const meetingDetails = useRef<IMeetingDetails | null>(null);
  const [meetingPage, setMeetingPage] = useState<MeetingPageType>(null);
  const [isMeetingEnded, setIsMeetingEnded] = useState(false);
  const [endMeetingCustomMessage, setEndMeetingCustomMessage] = useState('');
  const [openSidebar, setOpenSidebar] = useState<MeetingSidebarType>(null);
  const [attendees, setAttendees] = useState<Record<string, string>>({});
  const [isVoiceOnlyCall, setIsVoiceOnlyCall] = useState(false);
  const mediaPipelineID = useRef('');

  meetingManager.getAttendee = async (_attendeeID, externalAttendeeID) => {
    if (externalAttendeeID && attendees[externalAttendeeID]) {
      return {
        name: attendees[externalAttendeeID],
      };
    }

    return {
      name: 'Anonymous',
    };
  };

  const joinMeeting: IMeetingContext['joinMeeting'] = useCallback(async () => {
    if (meetingDetails.current) {
      // Initalize the `MeetingSessionConfiguration`
      const meetingSessionConfiguration = new MeetingSessionConfiguration(
        meetingDetails.current.Meeting,
        meetingDetails.current.Attendee,
      );

      setAttendeeID(meetingDetails.current.Attendee.AttendeeId);
      setIsMeetingEnded(false);
      setEndMeetingCustomMessage('');

      let options = {};
      if (meetingDetails.current.meeting_type === VOICE_ONLY_MEETING_TYPE) {
        options = {
          deviceLabels: DeviceLabels.Audio,
        };
      }

      // Create a `MeetingSession` using `join()` function with the `MeetingSessionConfiguration`
      await meetingManager.join(meetingSessionConfiguration, options);
    }
  }, [meetingManager, meetingDetails]);

  async function startMeeting() {
    setMeetingPage(MeetingPage.VIDEO_CALL);
    // Start the `MeetingSession` to join the meeting
    await meetingManager.start();
  }

  return (
    <MeetingContext.Provider
      value={{
        attendeeID,
        openSidebar,
        meetingPage,
        isMeetingEnded,
        endMeetingCustomMessage,
        isVoiceOnlyCall,
        meetingDetails,
        mediaPipelineID,
        setAttendeeID,
        setOpenSidebar,
        setMeetingPage,
        setIsMeetingEnded,
        setEndMeetingCustomMessage,
        setAttendees,
        setIsVoiceOnlyCall,
        joinMeeting,
        startMeeting,
      }}
    >
      {children}
    </MeetingContext.Provider>
  );
}

export function useMeetingContext() {
  const context = useContext(MeetingContext);

  if (!context) {
    throw new Error(
      'useMeetingContext must be used within a MeetingContextProvider',
    );
  }

  return context;
}
